import { useState } from "react";
import useSWRMutation from "swr/mutation";
import { ChevronDownIcon, PlusIcon } from "lucide-react";
import { Section } from "@/components/section";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { StateBadge } from "@/components/badge/state-badge";
import {
  allStateCodes,
  defaultStateFlow,
  flowStart,
  stateInfo,
} from "@/constants/version-state";
import { useVersionStates } from "@/hooks/version-states";
import { useIsAdmin } from "@/hooks/auth";
import { StateDialog } from "@/components/version-states/state-dialog";
import { putFetcher } from "@/lib/swr";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import type { App } from "@/types/domain";
import { refreshApps } from "./refresh-apps";
import { VersionFlowGraph } from "./version-flow-graph";

type Flow = Record<string, number[]>;

// Stable serialization for dirty/default checks (keys and nexts both
// sorted; the reserved `start` key rides along first).
const serialize = (flow: Flow) =>
  JSON.stringify([
    flowStart(flow),
    ...Object.keys(flow)
      .filter((k) => k !== "start")
      .map(Number)
      .sort((a, b) => a - b)
      .map((c) => [c, [...(flow[String(c)] ?? [])].sort((a, b) => a - b)]),
  ]);

// States reachable from the configured entry by walking nexts.
const reachableSet = (flow: Flow): Set<number> => {
  const entry = flowStart(flow);
  const seen = new Set<number>([entry]);
  const queue = [entry];
  while (queue.length) {
    const c = queue.shift() as number;
    for (const n of flow[String(c)] ?? []) {
      if (!seen.has(n)) {
        seen.add(n);
        queue.push(n);
      }
    }
  }
  return seen;
};

// Drop everything the start can no longer reach (their nexts only pointed
// inside the pruned group, so the remaining graph stays self-contained).
const prune = (flow: Flow): Flow => {
  const reach = reachableSet(flow);
  const out: Flow = { start: [flowStart(flow)] };
  for (const k of Object.keys(flow)) {
    if (k !== "start" && reach.has(Number(k))) out[k] = flow[k];
  }
  return out;
};

// Per-app version-state flow editor, built outward from a virtual Start
// node: Start points at the entry state (where a newly created version
// lands; 规划中 by default), picking a state as someone's "next" pulls it in
// as a new row to configure further, and states Start can't reach any more
// drop out of the list (and the flowchart) automatically. A state's nexts
// are simply the targets offered in the state dropdown — forward steps and
// rollbacks alike. Saving the catalog default graph stores null, so
// unconfigured apps keep following the default (and future default changes).
export const VersionFlowSection = ({ app }: { app: App }) => {
  const { toast } = useToast();
  // Re-render when the runtime catalog loads/changes (DB-managed states).
  const catalogRows = useVersionStates() ?? [];
  // Catalog writes are admin-gated on the server; hide the entry for others.
  const isAdmin = useIsAdmin();
  const [addingState, setAddingState] = useState(false);
  const defFlow = defaultStateFlow();
  // Legacy configs predate the reserved `start` key — default it to 0.
  const saved: Flow = app.versionStateFlow
    ? { start: [flowStart(app.versionStateFlow)], ...app.versionStateFlow }
    : defFlow;
  // null = no local edits yet; render the saved config.
  const [draft, setDraft] = useState<Flow | null>(null);
  const flow = draft ?? saved;
  const dirty = serialize(flow) !== serialize(saved);
  const isDefault = serialize(flow) === serialize(defFlow);

  const { trigger, isMutating } = useSWRMutation(
    `/apps/${app.id}/version-flow`,
    putFetcher,
    {
      onSuccess: () => {
        refreshApps();
        setDraft(null);
        toast({ title: "Version flow saved" });
      },
      onError: (err: unknown) => toastError(toast, "Save failed", err),
    }
  );

  const toggleNext = (code: number, target: number) => {
    const curr = flow[String(code)] ?? [];
    if (curr.includes(target)) {
      // Removing an arrow may strand states — prune what 0 can't reach.
      setDraft(
        prune({ ...flow, [String(code)]: curr.filter((n) => n !== target) })
      );
    } else {
      const next: Flow = {
        ...flow,
        [String(code)]: [...curr, target].sort((a, b) => a - b),
      };
      // First arrow into a state pulls it into the flow as a fresh row.
      if (!(String(target) in next)) next[String(target)] = [];
      setDraft(next);
    }
  };

  // Rows: reachable states only, in code order (code value = flow order).
  const reach = reachableSet(flow);
  const rows = allStateCodes().filter((c) => reach.has(c));
  const entry = flowStart(flow);

  const setStart = (c: number) => {
    const next: Flow = { ...flow, start: [c] };
    // The new entry must exist as a row before pruning walks from it.
    if (!(String(c) in next)) next[String(c)] = [];
    setDraft(prune(next));
  };

  return (
    <Section
      title="Version flow"
      action={
        <div className="flex items-center gap-2">
          {/* Creating here writes the same global catalog as Admin → States
              (codes mean the same thing in every app); wire an arrow to the
              new state to pull it into this app's flow, then Save. */}
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => setAddingState(true)}
            >
              <PlusIcon className="mr-1 h-3.5 w-3.5" />
              New state
            </Button>
          )}
          {!isDefault && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8"
              disabled={isMutating}
              onClick={() => setDraft(defFlow)}
            >
              Reset to default
            </Button>
          )}
          <Button
            size="sm"
            className="h-8"
            disabled={!dirty || isMutating}
            onClick={() => trigger({ flow: isDefault ? null : flow })}
          >
            {isMutating ? "Saving…" : "Save"}
          </Button>
        </div>
      }
    >
      <div className="mt-2">
        <VersionFlowGraph flow={flow} />
      </div>
      <div className="mt-3 flex flex-col gap-1">
        {/* Virtual Start node: where a newly created version lands. */}
        <div className="flex items-center gap-3 py-0.5">
          <span className="w-10 shrink-0" />
          <span className="w-16 shrink-0 rounded bg-primary px-1.5 py-0.5 text-center text-xs font-medium text-primary-foreground">
            Start
          </span>
          <span className="w-8 shrink-0" />
          <div className="flex min-w-0 items-center gap-2">
            <span className="text-xs text-muted-foreground">→</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 max-w-72 justify-between gap-1 px-2 text-xs font-normal"
                >
                  <span className="truncate">
                    {stateInfo(entry)?.label ?? entry}
                  </span>
                  <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-44">
                <DropdownMenuLabel className="text-xs">
                  New versions start in
                </DropdownMenuLabel>
                {allStateCodes().map((c) => (
                  <DropdownMenuCheckboxItem
                    key={c}
                    checked={c === entry}
                    onCheckedChange={() => setStart(c)}
                  >
                    <span className="flex w-full items-center justify-between gap-3">
                      {stateInfo(c)?.label ?? c}
                      <span className="font-mono text-xs tabular-nums text-muted-foreground">
                        {c}
                      </span>
                    </span>
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {rows.map((code) => {
          const nexts = flow[String(code)] ?? [];
          return (
            <div key={code} className="flex items-center gap-3 py-0.5">
              <span className="w-10 shrink-0" />
              <StateBadge state={code} className="w-16 text-center" />
              {/* Raw state code, for reference when wiring flows/APIs. */}
              <span className="w-8 shrink-0 text-right font-mono text-xs tabular-nums text-muted-foreground">
                {code}
              </span>
              <div className="flex min-w-0 items-center gap-2">
                <span className="text-xs text-muted-foreground">→</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 max-w-72 justify-between gap-1 px-2 text-xs font-normal"
                    >
                      <span className="truncate">
                        {nexts.length
                          ? nexts
                              .map((c) => stateInfo(c)?.label ?? c)
                              .join("、")
                          : "No next states"}
                      </span>
                      <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-44">
                    <DropdownMenuLabel className="text-xs">
                      {stateInfo(code)?.label} can move to
                    </DropdownMenuLabel>
                    {/* Whole catalog: picking an unused state pulls it in. */}
                    {allStateCodes()
                      .filter((c) => c !== code)
                      .map((c) => (
                        <DropdownMenuCheckboxItem
                          key={c}
                          checked={nexts.includes(c)}
                          // Keep the menu open so several targets can be
                          // toggled in one go.
                          onSelect={(e) => e.preventDefault()}
                          onCheckedChange={() => toggleNext(code, c)}
                        >
                          <span className="flex w-full items-center justify-between gap-3">
                            {stateInfo(c)?.label ?? c}
                            <span className="font-mono text-xs tabular-nums text-muted-foreground">
                              {c}
                            </span>
                          </span>
                        </DropdownMenuCheckboxItem>
                      ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          );
        })}
      </div>
      {addingState && (
        <StateDialog rows={catalogRows} onClose={() => setAddingState(false)} />
      )}
    </Section>
  );
};

export default VersionFlowSection;
